import { useForm } from "react-hook-form"
import { formValues } from "./validation";
import { COMMUNITY_ID, FREQUENCY, JOB_ID, PERIOD_FROM, PERIOD_TO, RUN_TYPE, SCHEDULED_ON, STATUS, YEAR } from "./consts";
import { useAddAdhocJobMutation, useGetJobById, useUpdateJobMutation } from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect } from "react";
import moment from "moment";

const useAddDistributionJob = ({jobId, isEdit, frequency, type, setOpen}: {jobId: string, isEdit: boolean, frequency: string, type: string, setOpen: any}) => {
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);

  const queryClient = useQueryClient();
  const form = useForm<formValues>({
    defaultValues: {
      [JOB_ID]: '',
      [STATUS]: '',
      [SCHEDULED_ON]: '',
      [PERIOD_FROM]: '',
      [PERIOD_TO]: '',
      [COMMUNITY_ID]: communityData?.communityId,
      [YEAR]: currentFinanceYear,
      [FREQUENCY]: frequency,
      [RUN_TYPE]: 'Periodic',
    }
  });

  const {data: jobData, isLoading: isLoadingJobData} = useGetJobById({
    communityId: communityData?.communityId,
    jobId: jobId,
    queryParams: {
      enabled: !!(jobId && isEdit)
    }
  });

  useEffect(() => {
    if (jobData?.data && isEdit) {
      form.reset({
        [JOB_ID]: jobData?.data?.[JOB_ID]?.toString() || '',
        [STATUS]: jobData?.data?.[STATUS] ? 'Active' : 'In-Active',
        [SCHEDULED_ON]: jobData?.data?.[SCHEDULED_ON] || '',
        [PERIOD_FROM]: jobData?.data?.[PERIOD_FROM] || '',
        [PERIOD_TO]: jobData?.data?.[PERIOD_TO] || '',
        [COMMUNITY_ID]: jobData?.data?.[COMMUNITY_ID]?.toString() || communityData?.communityId,
        [YEAR]: jobData?.data?.[YEAR]?.toString() || '',
        [FREQUENCY]: jobData?.data?.[FREQUENCY]?.toString() || frequency,
        [RUN_TYPE]: jobData?.data?.[RUN_TYPE]?.toString() || 'Periodic',
      });
    }
  }, [jobData]);

  const { mutate: addInfo, isPending: isAddPending } =
    useAddAdhocJobMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Adhoc Job Added Successfully");
        setOpen(false);
        queryClient.invalidateQueries({
          queryKey: ["distribution-jobs-list", communityData?.communityId, currentFinanceYear, type, frequency],
        });
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateJobMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Job Updated Successfully");
        setOpen(false);
        queryClient.invalidateQueries({
          queryKey: ["distribution-jobs-list", communityData?.communityId, currentFinanceYear, type, frequency],
        });
      },
    });

  const onSubmit = (data: formValues) => {
    delete data[RUN_TYPE];

    if (isEdit) {
      updateInfo({
        communityId: communityData?.communityId,
        jobId,
        data: {
          [YEAR]: data?.[YEAR],
          [SCHEDULED_ON]: data?.[SCHEDULED_ON] ? moment(data?.[SCHEDULED_ON]).format('YYYY-MM-DD') : null,
          [PERIOD_FROM]: data?.[PERIOD_FROM] ? moment(data?.[PERIOD_FROM]).format('YYYY-MM-DD') : null,
          [PERIOD_TO]: data?.[PERIOD_TO] ? moment(data?.[PERIOD_TO]).format('YYYY-MM-DD') : null,
        }
      })
    } else {
      addInfo({
        communityId: communityData?.communityId,
        data: {
          [YEAR]: data?.[YEAR],
          [SCHEDULED_ON]: data?.[SCHEDULED_ON] ? moment(data?.[SCHEDULED_ON]).format('YYYY-MM-DD') : null,
          [PERIOD_FROM]: data?.[PERIOD_FROM] ? moment(data?.[PERIOD_FROM]).format('YYYY-MM-DD') : null,
          [PERIOD_TO]: data?.[PERIOD_TO] ? moment(data?.[PERIOD_TO]).format('YYYY-MM-DD') : null,
          [COMMUNITY_ID]: data?.[COMMUNITY_ID]
        }
      })
    }
  }

  return {
    form,
    onSubmit,
    isAddPending,
    isLoadingJobData,
    isUpdatePending,
  }
}

export default useAddDistributionJob;